import { useState } from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { useLang } from "@/lib/i18n";
import { SectionLabel, Reveal } from "./Reveal";

const fmt = (n) => Math.round(n).toLocaleString("ru-RU").replace(/,/g, " ");
const OPEX = 215000;

const Row = ({ label, value, testid, big = false }) => (
  <div className="flex items-end justify-between gap-6 py-5 border-b border-white/10">
    <span className="text-sm text-[var(--muted)] max-w-[55%]">{label}</span>
    <motion.span key={value} initial={{ opacity: 0.3, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className={`font-display text-right ${big ? "text-3xl md:text-5xl text-[var(--gold)]" : "text-xl md:text-2xl text-[var(--text)]"}`} data-testid={testid}>
      {value} ₸
    </motion.span>
  </div>
);

export const Economics = () => {
  const { t } = useLang();
  const e = t.econ;
  const [subs, setSubs] = useState(2000);
  const [tariff, setTariff] = useState(2000);
  const gross = subs * tariff;
  const costs = OPEX + gross * 0.03;
  const net = Math.max(0, gross - costs);

  return (
    <section id="economics" className="relative py-28 md:py-40 bg-[var(--surface-2)] overflow-hidden" data-testid="economics-section">
      <div className="absolute -right-40 top-20 w-[520px] h-[520px] rounded-full bg-[var(--gold)]/10 blur-[120px]" aria-hidden="true" />
      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <SectionLabel n="05">{e.label}</SectionLabel>
          <Reveal>
            <h2 className="font-display text-3xl md:text-5xl mt-8 text-[var(--text)]">{e.title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base md:text-lg text-[var(--text)]/80 leading-relaxed">{e.lead}</p>
          </Reveal>
          <Reveal delay={0.2} className="mt-12 space-y-10 [--primary:38_76%_57%] [--background:222_33%_6%]">
            <div>
              <div className="flex justify-between text-sm mb-4">
                <span className="text-[var(--muted)]">{e.subs}</span>
                <span className="font-display text-[var(--text)]" data-testid="econ-subs-value">{fmt(subs)}</span>
              </div>
              <Slider data-testid="econ-subs-slider" value={[subs]} min={200} max={5000} step={100} onValueChange={(v) => setSubs(v[0])} />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-4">
                <span className="text-[var(--muted)]">{e.tariff}</span>
                <span className="font-display text-[var(--text)]" data-testid="econ-tariff-value">{fmt(tariff)} ₸</span>
              </div>
              <Slider data-testid="econ-tariff-slider" value={[tariff]} min={1000} max={6000} step={100} onValueChange={(v) => setTariff(v[0])} />
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-7 lg:pl-10">
          <div className="rounded-[28px] border border-white/10 bg-[var(--bg)]/80 backdrop-blur-xl p-6 md:p-10">
            <Row label={e.gross} value={fmt(gross)} testid="econ-gross" />
            <Row label={e.costs} value={`−${fmt(costs)}`} testid="econ-costs" />
            <Row label={e.net} value={fmt(net)} testid="econ-net" big />
            <Row label={e.year} value={fmt(net * 12)} testid="econ-year" />
            <p className="mt-6 text-xs text-[var(--muted)] leading-relaxed">{e.note}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
